<script setup lang="ts">
import {useElementor, useWp} from "~/composables/Redmonkey";
import TitleBase from "~/components/Redmonkey/Base/TitleBase.vue";
import Accordion from "~/components/Redmonkey/Base/Accordion.vue";
import DecorSidebar from "~/components/Redmonkey/Base/DecorSidebar.vue";
import StepsTextFlow from "~/components/Redmonkey/Base/StepsTextFlow.vue";
import CurvedBlock from "~/components/Redmonkey/Base/CurvedBlock.vue";
import DecorInGrid from "~/components/Redmonkey/Base/DecorInGrid.vue";
import Item from "~/components/Redmonkey/Courses/Item.vue";

const {
  getSettingsValue,
} = useElementor();

const {
  getMediaAltFromMedia,
  getMediaHeightFromMedia,
  getMediaUrlFromMedia,
  getMediaWidthFromMedia
} = useWp();

const props = defineProps({
  course: {
    type: Object,
    required: true
  }
})

const getTitle = () => {
  return getSettingsValue(props.course, 'title') || props.course?.title;
}

const getImage = () => {
  if (getSettingsValue(props.course, 'image', 'url')) {
    return {
      url: getSettingsValue(props.course, 'image', 'url'),
      alt: getSettingsValue(props.course, 'image', 'alt'),
      width: 'auto',
      height: 'auto'
    }
  }

  return {
    url: getMediaUrlFromMedia(props.course?.featuredImage?.node),
    alt: getMediaAltFromMedia(props.course?.featuredImage?.node),
    width: getMediaWidthFromMedia(props.course?.featuredImage?.node),
    height: getMediaHeightFromMedia(props.course?.featuredImage?.node)
  }
}

const getSteps = () => {
  return getSettingsValue(props.course, 'steps') || [];
}

const getDescription = () => {
  return getSettingsValue(props.course, 'description') || '' +
      '<p>В основі програми лежать реальні вимоги IT-ринку. Ми постійно працюємо над її покращенням і оновленням, щоб випускники ІТ Академії могли гідно показати себе перед роботодавцем.<br>Щоб отримати сертифікат, необхідно здати підсумковий проект. Але, для роботодавця показником професійності є не сертифікат, а Ваше портфоліо!</p>';
}


const getNextCoursesIds = () => {
  const ids = getSettingsValue(props.course, 'choose_courses');

  let returnIds = ids || false;

  if (ids) {
    returnIds = [];

    for (let i in ids) {
      returnIds.push(parseInt(ids[i]));
    }
  }

  return returnIds;
}


const getCoursesData = async () => {
  const ids = getNextCoursesIds();
  if (ids) {
    return await useFetch('/api/wordpress/getCourses', {
      method: "post",
      body: {
        first: ids.length,
        ids: ids
      }
    })
  } else {
    return await useFetch('/api/wordpress/getCourses', {
      method: "post",
      body: {
        first: 3,
        notIds: [props.course.id]
      }
    })
  }
}

const {data} = await getCoursesData();

const nextCourses = computed(() => data.value?.courses?.nodes || [])

const getLessonsCount = () => {
  return props.course?.courses?.kilkistZanyat;
}

const getPrice = () => {
  return props.course?.courses?.price;
}

const isPartPayments = () => {
  return props.course?.courses?.oplataChastynamy;
}

const getDuration = () => {
  return props.course?.courses?.tryvalist;
}

const getCourseAboutTitle = () => {
  return getSettingsValue(props.course, 'title_about') || 'Опис Курсу';
}

const getCourseAbout1 = () => {
  return getSettingsValue(props.course, 'about_1') || props.course?.courses?.smallDescription;
}

const getCourseAbout2 = () => {
  return getSettingsValue(props.course, 'about_2');
}

const getModulesTitle = () => {
  return getSettingsValue(props.course, 'title_program') || 'Програма Курсу';
}

const getModules = () => {
  return getSettingsValue(props.course, 'modules') || [];
}

const getStepsTitle = () => {
  return getSettingsValue(props.course, 'title_what_you_get') || 'Що ви отримаєте';
}
</script>
<template>
  <div class="single-course single">
    <h1 v-if="getTitle()">{{ getTitle() }}</h1>
    <div class="event-intro flex wrap justify">
      <div class="event-info">
        <ul>
          <li class="date-time" v-if="getDuration()">Тривалість: <span>{{ `${getDuration()}` }}</span></li>
          <li class="count" v-if="getLessonsCount()">Кількість занять: <span>{{ `${getLessonsCount()}` }}</span></li>
          <li class="price" v-if="getPrice()">Ціна: <span>{{ getPrice() }}</span><span class="more"
                                                                                       v-if="isPartPayments()"><br>(Можлива оплата частинами)</span>
          </li>
        </ul>
      </div>
      <div class="event-img">
        <picture>
          <img v-if="getImage().url" :alt="getImage().alt" :src="getImage().url" :width="getImage().width"
               :height="getImage().height"/>
        </picture>
      </div>
      <div class="event-decor">
        <img src="/images/hexagon.png" :alt="getTitle()"/>
      </div>
    </div>
    <div class="about-course" v-if="getCourseAboutTitle() && getCourseAbout1()">
      <h2 class="title">{{ getCourseAboutTitle() }}</h2>
      <div class="about-course-items flex wrap justify">
        <div class="about-course-item" v-if="getCourseAbout1()" v-html="getCourseAbout1()"></div>
        <div class="about-course-item" v-if="getCourseAbout2()" v-html="getCourseAbout2()"></div>
      </div>
    </div>
    <div class="course-program" v-if="getModules().length">
      <TitleBase v-if="getModulesTitle()" :title="getModulesTitle()" position="center" star-position="right"/>
      <div class="course-program-modules flex wrap faq">
        <div class="decor-sidebar-wrap">
          <div class="event-intro">
            <div class="event-info">
              <ul>
                <li class="date-time" v-if="getDuration()">Тривалість: <span>{{ `${getDuration()}` }}</span></li>
                <li class="count" v-if="getLessonsCount()">Кількість занять: <span>{{ `${getLessonsCount()}` }}</span>
                </li>
                <li class="price" v-if="getPrice()">Ціна: <span>{{ getPrice() }}</span><span class="more"
                                                                                             v-if="isPartPayments()"><br>(Можлива оплата частинами)</span>
                </li>
              </ul>
            </div>
          </div>
          <DecorSidebar/>
        </div>
        <div class="faq-section">
          <div class="faq-item" v-for="(faq, index) in getModules()" :key="faq.title">
            <Accordion :open="index === 0">
              <template #head>
                <h3>{{ faq.title }}</h3>
                <div class="num">
                  <span class="hide-mobile hide-ipad-p">{{ `Модуль ${index + 1}` }}</span>
                  <span class="hide-desktop hide-medium">{{ `М${index + 1}` }}</span>
                </div>
              </template>
              <template #body>
                <div v-html="faq.about"></div>
              </template>
            </Accordion>
          </div>
        </div>
      </div>
    </div>
    <div class="course-steps" v-if="getSteps().length">
      <TitleBase v-if="getStepsTitle()" :title="getStepsTitle()" position="center" star-position="right"/>
      <StepsTextFlow :steps="getSteps()" step-color="blue"/>
    </div>
    <div class="course-description" v-if="getDescription()">
      <div class="course-description-container">
        <CurvedBlock type="yellow" :content="getDescription()"/>
        <DecorInGrid title="навіть не посперечаєшся"/>
      </div>
      <div class="course-description-button">
        <nuxt-link class="action secondary" :to="{ hash: '#demo-form' }">
          Записатись
        </nuxt-link>
      </div>
    </div>
    <div class="next-courses courses-list courses-short" v-if="nextCourses">
      <TitleBase title="Наші інші курси" position="left" star-position="right"/>
      <div class="next-courses-items flex wrap">
        <template v-for="item in nextCourses" :key="item.id">
          <Item
              :title="item.title"
              :content="item.courses.smallDescription"
              :age="item.courses.age"
              :price="item.courses.price"
              :url="item.uri"
          />
        </template>
      </div>
      <div class="all-courses-button">
        <nuxt-link class="action primary" to="/courses">
          Більше курсів
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import "assets/css/source/components/single";
</style>